import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { useSubscribe } from '../../utils';
import Loader from '../Loader';

function createLoadingContext() {
  const references = new Set();
  const isLoading$ = new BehaviorSubject(false);
  const setLoading = (ref, isLoading = false) => {
    if (isLoading) {
      references.add(ref);
    } else {
      references.delete(ref);
    }
    isLoading$.next(references.size > 0);
  };
  return {
    setLoading,
    isLoading$: isLoading$.pipe(distinctUntilChanged()),
  };
}
const loadingContext = createContext(createLoadingContext());
export const LoadingProvider = ({ children }) => {
  const context = useMemo(createLoadingContext, []);
  return <loadingContext.Provider value={context}>{children}</loadingContext.Provider>;
};

export const useSetLoading = () => {
  const ref = useMemo(() => Symbol('Loading reference'), []);
  const { setLoading } = useContext(loadingContext);
  const callback = useCallback(
    (isLoading) => {
      setLoading(ref, isLoading);
    },
    [ref]
  );
  useEffect(() => () => callback(false), [callback]);
  return callback;
};

export const useIsLoading = () => {
  const { isLoading$ } = useContext(loadingContext);
  return useSubscribe(isLoading$, false);
};

const LoadingOverlay = () => {
  const isLoading = useIsLoading();
  if (!isLoading) {
    return null;
  }
  return <Loader />;
};

export const LoadingLayer = ({ children }) => (
  <div className="loading-layer">
    <LoadingProvider>
      {children}
      <LoadingOverlay />
    </LoadingProvider>
  </div>
);
